// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@carbon/icons-react';
import { useProcedureLibrary, useCreateProcedure, isProcedureValid } from '../../shared/procedures';
import ProcedureForm from '../components/ProcedureForm';
import ProcedureFormFooter from '../components/ProcedureFormFooter';
import ProcedurePreview from '../components/ProcedurePreview';
import ProcedureJsonPane from '../components/ProcedureJsonPane';
import StatusTag from '../components/StatusTag';
import type { ProcedureDetail } from '../../shared/types/procedures';
// Same page layout as ProcedureViewPage (breadcrumb, tabs, panes, footer) —
// reusing its CSS module rather than duplicating it.
import styles from './ProcedureViewPage.module.css';

function blankProcedure(): ProcedureDetail {
  return {
    artifactId: '',
    artifactType: 'sop',
    title: '',
    procedureNumber: '',
    version: '0.1',
    status: 'draft',
    classification: '',
    equipmentType: '',
    description: '',
    affectedEquipment: [],
    prerequisites: { qualifications: '', approvals: '', precondition: '' },
    procedure: [],
    contingencies: [],
    closeout: { turnoverFrom: '', turnoverTo: '', signoffRole: '' },
    governance: {
      mopRequired: false,
      changeApproval: false,
      loto: false,
      redundancy: '',
      escalatesToEop: '',
    },
  };
}

/**
 * "Start a blank procedure" screen — route `procedures/new`. Same Guided /
 * Live Preview / Advanced-JSON layout as `ProcedureViewPage`, seeded with a
 * blank draft instead of a fetched artifact. No backend create endpoint
 * exists yet (see `sopFactoryService.updateArtifact`'s own docstring for the
 * equivalent update-side gap) — Submit just navigates back to Library once
 * the draft is valid, the same "no backend yet" convention used there.
 */
export default function CreateProcedurePage() {
  const navigate = useNavigate();
  const { domainFacets, equipmentFacets } = useProcedureLibrary({});
  const { create, isCreating, error: createError } = useCreateProcedure();
  const [blank] = useState(blankProcedure);
  const [procedure, setProcedure] = useState<ProcedureDetail>(blank);
  const [tab, setTab] = useState<0 | 1>(0);

  // `ProcedureFormFooter`'s Patch/Minor bump is for revising an *existing*
  // approved version — meaningless on a brand-new v0.1 draft, so the version
  // it computes is ignored here in favor of the draft's own version field.
  async function handleSubmit() {
    try {
      await create(procedure);
      navigate('/admin-center/procedures/library');
    } catch {
      // Surfaced via `createError` in the footer — stay on the page so
      // nothing typed gets lost.
    }
  }

  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.backLink}
        onClick={() => navigate('/admin-center/procedures/library')}
      >
        <ArrowLeft size={16} /> Back to Library
      </button>

      <div className={styles.titleRow}>
        <h1 className={styles.title}>New SOP</h1>
        <div className={styles.switcher} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 0}
            className={`${styles.switchBtn} ${tab === 0 ? styles.switchBtnSelected : ''}`}
            onClick={() => setTab(0)}
          >
            Guided
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 1}
            className={`${styles.switchBtn} ${tab === 1 ? styles.switchBtnSelected : ''}`}
            onClick={() => setTab(1)}
          >
            Advanced: raw JSON
          </button>
        </div>
      </div>

      <div className={styles.subtitle}>
        <span>{procedure.artifactType.toUpperCase()}</span>
        <span aria-hidden="true">·</span>
        <StatusTag status={procedure.status} />
      </div>

      <div className={styles.panes}>
        <div className={styles.pane}>
          <div className={styles.paneScroll}>
            {tab === 0 ? (
              <ProcedureForm
                procedure={procedure}
                original={blank}
                classificationOptions={domainFacets.map(f => f.value)}
                equipmentTypeOptions={equipmentFacets.map(f => f.value)}
                onChange={setProcedure}
                hideChanges
              />
            ) : (
              <ProcedureJsonPane procedure={procedure} />
            )}
          </div>
          <ProcedureFormFooter
            version={procedure.version}
            onCancel={() => navigate('/admin-center/procedures/library')}
            onSubmit={handleSubmit}
            isSaving={isCreating}
            canSubmit={isProcedureValid(procedure)}
            error={createError}
          />
        </div>
        <div className={`${styles.pane} ${styles.panePreview}`}>
          <div className={styles.paneScroll}>
            <ProcedurePreview procedure={procedure} />
          </div>
        </div>
      </div>
    </div>
  );
}
